import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL } from './daily-entry-ui.js';

interface AttendanceEntry {
  status?: string;
}

interface ClockEntry {
  clock_in_time?: string;
  clock_out_time?: string;
  clock_in_timestamp?: string;
  clock_out_timestamp?: string;
}

interface RosterDriver {
  id: string | number;
  first_name?: string;
  last_name?: string;
  driver_id?: string;
  assigned_terminal_name?: string | null;
  assigned_terminal_id?: string | number;
  staffing_type?: string;
}

interface DailyEntryRosterProps {
  roster: RosterDriver[];
  attendanceByEmployee: Record<string, AttendanceEntry | undefined>;
  clockByEmployee: Record<string, ClockEntry | undefined>;
  search: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onWorked: (driver: RosterDriver) => void | Promise<void>;
  onAbsent: (driver: RosterDriver) => void | Promise<void>;
  onEdit?: (driver: RosterDriver, entry: AttendanceEntry) => void | Promise<void>;
}

function statusPills(attendanceEntry: AttendanceEntry | undefined, clockEntry: ClockEntry | undefined) {
  const pills = [];
  if (attendanceEntry) {
    pills.push({ label: 'Entered', color: C.green, background: 'rgba(78,205,196,0.12)' });
    if (attendanceEntry.status) {
      pills.push({ label: attendanceEntry.status, color: C.gold, background: 'rgba(201,162,39,0.12)' });
    }
  } else {
    pills.push({ label: 'Remaining', color: C.textMuted, background: 'rgba(255,255,255,0.05)' });
  }

  if (clockEntry?.clock_in_time && !clockEntry?.clock_out_time) {
    pills.push({ label: 'Clocked in', color: C.green, background: 'rgba(78,205,196,0.12)' });
  }
  if (clockEntry?.clock_out_time) {
    pills.push({ label: 'Clocked out', color: C.gold, background: 'rgba(201,162,39,0.12)' });
  }

  return pills;
}

export default function DailyEntryRoster({ roster, attendanceByEmployee, clockByEmployee, search, statusFilter, onSearchChange, onStatusFilterChange, onWorked, onAbsent, onEdit }: DailyEntryRosterProps) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
        Daily Entry roster read surface
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={MUTED_LABEL}>Search roster</label>
          <input type="text" value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Search drivers..." style={CONTROL_INPUT} />
        </div>
        <div>
          <label style={MUTED_LABEL}>Status filter</label>
          <select value={statusFilter} onChange={(event) => onStatusFilterChange(event.target.value)} style={CONTROL_INPUT}>
            <option value="all">All</option>
            <option value="remaining">Remaining</option>
            <option value="entered">Entered</option>
          </select>
        </div>
      </div>

      {roster.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: 13 }}>No Daily Entry roster rows for the current context.</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {roster.map((driver: RosterDriver) => {
            const attendanceEntry = attendanceByEmployee[driver.id];
            const clockEntry = clockByEmployee[driver.id];
            const pills = statusPills(attendanceEntry, clockEntry);

            return (
              <div key={driver.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{driver.first_name} {driver.last_name}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>{driver.driver_id || 'No driver ID'} · {driver.assigned_terminal_name || driver.assigned_terminal_id || 'No terminal'}</div>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 12 }}>{driver.staffing_type || 'local'}</div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {pills.map((pill) => (
                    <span key={pill.label} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, color: pill.color, background: pill.background }}>
                      {pill.label}
                    </span>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                  <div style={{ color: C.textMuted, fontSize: 12 }}>
                    Attendance status: <span style={{ color: C.text }}>{attendanceEntry?.status || 'none'}</span>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 12 }}>
                    Clock in: <span style={{ color: C.text }}>{clockEntry?.clock_in_timestamp || clockEntry?.clock_in_time || '—'}</span>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 12 }}>
                    Clock out: <span style={{ color: C.text }}>{clockEntry?.clock_out_timestamp || clockEntry?.clock_out_time || '—'}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => onWorked(driver)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: C.gold, color: '#111', fontWeight: 600, cursor: 'pointer' }}>
                    Worked
                  </button>
                  <button type="button" onClick={() => onAbsent(driver)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                    Absent
                  </button>
                  {attendanceEntry && onEdit && (
                    <button type="button" onClick={() => onEdit(driver, attendanceEntry)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: C.panel, color: C.text, fontWeight: 600, cursor: 'pointer' }}>
                      Edit
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
